<template>
  <div class="modal-backdrop" @click.self="$emit('close')">
    <div class="modal-custom">
      <div class="modal-header">
        <h5>Manage Members - {{ group.name }}</h5>
        <el-button @click="$emit('close')" circle plain>
          <i class="fas fa-times"></i>
        </el-button>
      </div>
      
      <div class="modal-body">
        <el-tabs v-model="activeTab">
          <el-tab-pane label="Current Members" name="current-members">
            <div v-if="group.members && group.members.length > 0" class="member-list">
              <el-table :data="group.members" style="width: 100%">
                <el-table-column prop="name" label="Name"></el-table-column>
                <el-table-column prop="user_type" label="Type"></el-table-column>
                <el-table-column label="Status" width="120">
                  <template #default="{row}">
                    <el-tag :type="row.is_online ? 'success' : 'info'" size="small">
                      {{ row.is_online ? 'Online' : 'Offline' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="Actions" width="180" v-if="isGroupAdmin">
                  <template #default="{row}">
                    <el-button 
                      v-if="row.pivot.is_admin"
                      @click="updateMemberRole(row, false)"
                      size="small"
                      type="warning"
                      plain
                    >
                      Demote
                    </el-button>
                    <el-button 
                      v-else
                      @click="updateMemberRole(row, true)"
                      size="small"
                      type="success"
                      plain
                    >
                      Promote
                    </el-button>
                    <el-button 
                      @click="removeMember(row)"
                      size="small"
                      type="danger"
                      plain
                    >
                      Remove
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
            <el-empty v-else description="No members in this group"></el-empty>
          </el-tab-pane>
          
          <el-tab-pane label="Add Members" name="add-members">
            <div class="add-members-container">
              <div class="search-filters">
                <el-input
                  v-model="searchQuery"
                  placeholder="Search users..."
                  clearable
                  @clear="filterUsers"
                  @keyup.enter="filterUsers"
                >
                  <template #append>
                    <el-button @click="filterUsers">
                      <i class="fas fa-search"></i>
                    </el-button>
                  </template>
                </el-input>
                
                <div class="filter-options">
                  <el-select
                    v-model="filterClass"
                    placeholder="Filter by class"
                    clearable
                    @change="fetchSections"
                  >
                    <el-option
                      v-for="classItem in classes"
                      :key="classItem.id"
                      :label="classItem.name"
                      :value="classItem.id"
                    ></el-option>
                  </el-select>
                  
                  <el-select
                    v-model="filterSection"
                    placeholder="Filter by section"
                    clearable
                    :disabled="!filterClass"
                  >
                    <el-option
                      v-for="section in filteredSections"
                      :key="section.id"
                      :label="section.name"
                      :value="section.id"
                    ></el-option>
                  </el-select>
                  
                  <el-select
                    v-model="filterUserType"
                    placeholder="Filter by type"
                    clearable
                  >
                    <el-option
                      v-for="type in userTypes"
                      :key="type"
                      :label="type"
                      :value="type"
                    ></el-option>
                  </el-select>
                </div>
              </div>
              
              <div class="user-selection">
                <el-table
                  :data="filteredUsers"
                  style="width: 100%"
                  @selection-change="handleSelectionChange"
                  v-loading="loading"
                >
                  <el-table-column type="selection" width="55"></el-table-column>
                  <el-table-column prop="name" label="Name"></el-table-column>
                  <el-table-column prop="user_type" label="Type"></el-table-column>
                  <el-table-column label="Status" width="120">
                    <template #default="{row}">
                      <el-tag :type="row.is_online ? 'success' : 'info'" size="small">
                        {{ row.is_online ? 'Online' : 'Offline' }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="Details" width="180">
                    <template #default="{row}">
                      <div v-if="row.student">
                        Class: {{ row.student.class?.name }}
                        <span v-if="row.student.section"> - Section: {{ row.student.section.name }}</span>
                      </div>
                      <div v-if="row.employee">
                        <span v-for="(classItem, index) in row.employee.classes" :key="classItem.id">
                          {{ classItem.name }}<span v-if="index < row.employee.classes.length - 1">, </span>
                        </span>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
                
                <div class="selection-actions" v-if="selectedUsers.length > 0">
                  <el-button 
                    type="primary" 
                    @click="addSelectedMembers"
                    :loading="addingMembers"
                  >
                    Add {{ selectedUsers.length }} Selected Members
                  </el-button>
                  <el-button @click="selectedUsers = []">Clear Selection</el-button>
                </div>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

export default {
  props: {
    group: {
      type: Object,
      required: true
    },
    classes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      activeTab: 'current-members',
      allUsers: [],
      filteredUsers: [],
      selectedUsers: [],
      searchQuery: '',
      filterClass: null,
      filterSection: null,
      filterUserType: '',
      sections: [],
      loading: false,
      addingMembers: false,
      currentUser: JSON.parse(localStorage.getItem('user')) || {}
    };
  },
  computed: {
    filteredSections() {
      if (!this.filterClass) return [];
      return this.sections.filter(section => section.class_id == this.filterClass);
    },
    userTypes() {
      return [...new Set(this.allUsers.map(user => user.user_type))];
    },
    isGroupAdmin() {
      if (!this.group || !this.group.members || !this.currentUser?.id) return false;
      const member = this.group.members.find(m => m.id === this.currentUser.id);
      return member ? member.pivot?.is_admin : false;
    }
  },
  async created() {
    await this.fetchAllUsers();
  },
  watch: {
    filterClass() {
      this.filterUsers();
    },
    filterSection() {
      this.filterUsers();
    },
    filterUserType() {
      this.filterUsers();
    }
  },
  methods: {
    async fetchAllUsers() {
      this.loading = true;
      try {
        const response = await api.get('/users');
        this.allUsers = response.data?.data || [];
        this.filteredUsers = this.allUsers.filter(user => 
          !this.group.members.some(member => member.id === user.id)
        );
      } catch (error) {
        console.error('Error fetching users:', error);
        this.allUsers = [];
        this.filteredUsers = [];
      } finally {
        this.loading = false;
      }
    },
    
    async fetchSections() {
      if (!this.filterClass) {
        this.sections = [];
        this.filterSection = null;
        return;
      }
      
      try {
        const response = await api.get(`/classes/${this.filterClass}/sections`);
        this.sections = response.data?.sections || [];
      } catch (error) {
        console.error('Error fetching sections:', error);
        this.sections = [];
      }
    },
    
    async filterUsers() {
      this.loading = true;
      
      try {
        let filtered = this.allUsers.filter(user => 
          !this.group.members.some(member => member.id === user.id)
        );
        
        // Apply search query filter
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          filtered = filtered.filter(user => 
            user.name.toLowerCase().includes(query) || 
            user.user_type.toLowerCase().includes(query) ||
            (user.student?.class?.name?.toLowerCase().includes(query)) ||
            (user.student?.section?.name?.toLowerCase().includes(query))
          );
        }
        
        // Apply class/section filter
        if (this.filterClass) {
          filtered = filtered.filter(user => {
            // Check if student is in the class/section
            if (user.student && user.student.class_id == this.filterClass) {
              if (!this.filterSection) return true;
              return user.student.section_id == this.filterSection;
            }
            
            // Check if teacher is assigned to the class/section
            if (user.employee && user.employee.classes) {
              const hasClass = user.employee.classes.some(c => c.id == this.filterClass);
              if (!hasClass) return false;
              if (!this.filterSection) return true;
              
              // Note: This assumes employee has sections relationship
              // You may need to adjust based on your actual data structure
              return true;
            }
            
            return false;
          });
        }
        
        // Apply user type filter
        if (this.filterUserType) {
          filtered = filtered.filter(user => user.user_type === this.filterUserType);
        }
        
        this.filteredUsers = filtered;
      } catch (error) {
        console.error('Error filtering users:', error);
        this.filteredUsers = [];
      } finally {
        this.loading = false;
      }
    },
    
    handleSelectionChange(selection) {
      this.selectedUsers = selection;
    },
    
    async addSelectedMembers() {
      if (this.selectedUsers.length === 0) return;
      
      this.addingMembers = true;
      try {
        const userIds = this.selectedUsers.map(user => user.id);
        await api.post(`/chat-groups/${this.group.id}/members`, {
          user_ids: userIds
        });
        
        // Refresh group data
        const response = await api.get(`/chat-groups/${this.group.id}`);
        this.$emit('group-updated', response.data);
        
        this.$message.success(`${userIds.length} members added successfully`);
        this.selectedUsers = [];
        await this.fetchAllUsers(); // Refresh user list
      } catch (error) {
        console.error('Error adding members:', error);
        this.$message.error('Failed to add members');
      } finally {
        this.addingMembers = false;
      }
    },
    
    async removeMember(member) {
      try {
        await this.$confirm(`Remove ${member.name} from group?`, 'Confirm', {
          confirmButtonText: 'Remove',
          cancelButtonText: 'Cancel',
          type: 'warning'
        });
        
        await api.post(`/chat-groups/${this.group.id}/remove-members`, {
          user_ids: [member.id]
        });
        
        // Refresh group data
        const response = await api.get(`/chat-groups/${this.group.id}`);
        this.$emit('group-updated', response.data);
        
        this.$message.success('Member removed successfully');
        await this.fetchAllUsers(); // Refresh user list
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error removing member:', error);
          this.$message.error('Failed to remove member');
        }
      }
    },
    
    async updateMemberRole(member, asAdmin) {
      const action = asAdmin ? 'promote' : 'demote';
      
      try {
        await this.$confirm(`${action} ${member.name}?`, 'Confirm', {
          confirmButtonText: action === 'promote' ? 'Promote' : 'Demote',
          cancelButtonText: 'Cancel',
          type: 'warning'
        });
        
        await api.post(`/chat-groups/${this.group.id}/update-role`, {
          user_ids: [member.id],
          as_admin: asAdmin
        });
        
        // Refresh group data
        const response = await api.get(`/chat-groups/${this.group.id}`);
        this.$emit('group-updated', response.data);
        
        this.$message.success(`Member ${action}d successfully`);
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error updating member role:', error);
          this.$message.error(`Failed to ${action} member`);
        }
      }
    }
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999999999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-custom {
  background: white;
  border-radius: 8px;
  width: 800px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h5 {
  margin: 0;
  font-size: 1.2rem;
}

.modal-body {
  padding: 20px;
}

.search-filters {
  margin-bottom: 20px;
}

.filter-options {
  margin-top: 15px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.user-selection {
  margin-top: 20px;
}

.selection-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.member-list {
  margin-top: 15px;
}

.el-table {
  margin-top: 10px;
}

.el-tag {
  margin-right: 5px;
}
</style>